import { create } from 'zustand';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  dismissible?: boolean;
}

interface Modal {
  id: string;
  isOpen: boolean;
  data?: unknown;
}

interface UIState {
  // Navigation
  isMobileNavOpen: boolean;

  // Loading states
  isPageLoading: boolean;
  loadingTasks: Set<string>;

  // Notifications
  notifications: Notification[];

  // Modals
  modals: Record<string, Modal>;

  // Search
  globalSearchOpen: boolean;
  globalSearchQuery: string;
}

interface UIActions {
  // Navigation
  toggleMobileNav: () => void;
  setMobileNavOpen: (open: boolean) => void;

  // Loading
  setPageLoading: (loading: boolean) => void;
  addLoadingTask: (taskId: string) => void;
  removeLoadingTask: (taskId: string) => void;
  isTaskLoading: (taskId: string) => boolean;

  // Notifications
  addNotification: (notification: Omit<Notification, 'id'>) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Modals
  openModal: (id: string, data?: unknown) => void;
  closeModal: (id: string) => void;
  toggleModal: (id: string) => void;
  isModalOpen: (id: string) => boolean;
  getModalData: <T>(id: string) => T | undefined;

  // Search
  openGlobalSearch: () => void;
  closeGlobalSearch: () => void;
  setGlobalSearchQuery: (query: string) => void;
}

type UIStore = UIState & UIActions;

export const useUIStore = create<UIStore>((set, get) => ({
  // Initial state
  isMobileNavOpen: false,
  isPageLoading: false,
  loadingTasks: new Set(),
  notifications: [],
  modals: {},
  globalSearchOpen: false,
  globalSearchQuery: '',

  // Navigation actions
  toggleMobileNav: () => set((state) => ({ isMobileNavOpen: !state.isMobileNavOpen })),
  setMobileNavOpen: (open) => set({ isMobileNavOpen: open }),

  // Loading actions
  setPageLoading: (loading) => set({ isPageLoading: loading }),
  addLoadingTask: (taskId) =>
    set((state) => ({
      loadingTasks: new Set(state.loadingTasks).add(taskId),
    })),
  removeLoadingTask: (taskId) =>
    set((state) => {
      const tasks = new Set(state.loadingTasks);
      tasks.delete(taskId);
      return { loadingTasks: tasks };
    }),
  isTaskLoading: (taskId) => get().loadingTasks.has(taskId),

  // Notification actions
  addNotification: (notification) => {
    const id = `notification-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    set((state) => ({
      notifications: [
        ...state.notifications,
        { ...notification, id, dismissible: notification.dismissible ?? true },
      ],
    }));

    // Auto-dismiss after duration
    const duration = notification.duration ?? 5000;
    if (duration > 0) {
      setTimeout(() => {
        get().removeNotification(id);
      }, duration);
    }

    return id;
  },
  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),
  clearNotifications: () => set({ notifications: [] }),

  // Modal actions
  openModal: (id, data) =>
    set((state) => ({
      modals: {
        ...state.modals,
        [id]: { id, isOpen: true, data },
      },
    })),
  closeModal: (id) =>
    set((state) => ({
      modals: {
        ...state.modals,
        [id]: { ...state.modals[id], id, isOpen: false },
      },
    })),
  toggleModal: (id) => {
    const { modals, openModal, closeModal } = get();
    if (modals[id]?.isOpen) {
      closeModal(id);
    } else {
      openModal(id);
    }
  },
  isModalOpen: (id) => get().modals[id]?.isOpen ?? false,
  getModalData: <T>(id: string) => get().modals[id]?.data as T | undefined,

  // Search actions
  openGlobalSearch: () => set({ globalSearchOpen: true }),
  closeGlobalSearch: () => set({ globalSearchOpen: false, globalSearchQuery: '' }),
  setGlobalSearchQuery: (query) => set({ globalSearchQuery: query }),
}));

// Selectors
export const selectIsMobileNavOpen = (state: UIStore) => state.isMobileNavOpen;
export const selectIsPageLoading = (state: UIStore) => state.isPageLoading;
export const selectNotifications = (state: UIStore) => state.notifications;
export const selectGlobalSearchOpen = (state: UIStore) => state.globalSearchOpen;
